/**
 * VoiceRange Audio Engine v3
 * - 실시간 피치 감지 + 목표음 판정
 * - 1초 홀드 기준 (저지연)
 * - 노이즈 억제 + VAD(음성 구간 감지)
 * - 환경 소음 측정
 */

import { detectPitchYIN, calculateRMS, detectClipping, hzToNote, midiToNoteName, midiToHz, hzToMidi, PitchResult } from './pitch-detector';

/* ── 인터페이스 ── */

export interface AudioEngineConfig {
    sampleRate?: number;
    bufferSize?: number;
    targetTolerance?: number;
    holdDuration?: number;
    minConfidence?: number;
    noiseSuppression?: boolean;
    renderThrottle?: number;    // ms, 렌더링 콜백 최소 간격
}

export interface EnvironmentResult {
    rmsLevel: number;
    peakLevel: number;
    hasClipping: boolean;
    quality: 'quiet' | 'moderate' | 'noisy';
    score: number;
}

export interface PitchEvent {
    hz: number;
    midi: number;
    noteName: string;
    rms: number;
    confidence: number;
    isInTarget: boolean;
    holdProgress: number;
    cents: number;
    isVoice: boolean;
    octave: number;
    noteNumber: number;
}

export interface TestResult {
    lowestNote: number;
    highestNote: number;
    comfortableLow: number;
    comfortableHigh: number;
    voiceType: string;
    qualityScore: number;
    environmentScore: number;
    allSuccessNotes: number[];
}

/* ── 기본값 ── */

const DEFAULT_CONFIG: Required<AudioEngineConfig> = {
    sampleRate: 44100,
    bufferSize: 2048,
    targetTolerance: 1.0,
    holdDuration: 1000,
    minConfidence: 0.65,
    noiseSuppression: true,
    renderThrottle: 50,
};

/* ── AudioEngine ── */

export class AudioEngine {
    private config: Required<AudioEngineConfig>;
    private audioContext: AudioContext | null = null;
    private mediaStream: MediaStream | null = null;
    private sourceNode: MediaStreamAudioSourceNode | null = null;
    private scriptProcessor: ScriptProcessorNode | null = null;
    private running = false;

    // 목표음
    private targetMidi = 0;
    private holdStartTime = 0;
    private holdAccumulated = 0;
    private lastInTarget = false;

    // 환경/VAD
    private envNoiseFloor = 0;
    private envScore = 100;
    private vadThreshold = 0.02;
    private recentRms: number[] = [];

    // 렌더링 쓰로틀
    private lastRenderTime = 0;

    // 콜백
    private pitchCallback: ((e: PitchEvent) => void) | null = null;
    private noteHeldCallback: ((midi: number) => void) | null = null;

    // 통계
    private totalFrames = 0;
    private voicedFrames = 0;
    private confidenceSum = 0;

    constructor(config: Partial<AudioEngineConfig> = {}) {
        this.config = { ...DEFAULT_CONFIG, ...config } as Required<AudioEngineConfig>;
    }

    /* ── 콜백 등록 ── */

    public onPitch(cb: (e: PitchEvent) => void) { this.pitchCallback = cb; }
    public onNoteHeld(cb: (midi: number) => void) { this.noteHeldCallback = cb; }

    /* ── 브라우저 호환 체크 ── */

    public async checkBrowserSupport(): Promise<{ supported: boolean; issues: string[] }> {
        const issues: string[] = [];
        if (!navigator.mediaDevices?.getUserMedia) issues.push('getUserMedia not supported');
        if (!window.AudioContext && !(window as any).webkitAudioContext) issues.push('AudioContext not supported');
        if (location.protocol !== 'https:' && location.hostname !== 'localhost') issues.push('HTTPS required');
        return { supported: issues.length === 0, issues };
    }

    /* ── 마이크 ── */

    public async requestMicrophone(): Promise<boolean> {
        try {
            const constraints: any = {
                audio: {
                    echoCancellation: this.config.noiseSuppression,
                    noiseSuppression: this.config.noiseSuppression,
                    autoGainControl: this.config.noiseSuppression,
                }
            };
            this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
            return true;
        } catch { return false; }
    }

    /* ── 환경 소음 측정 ── */

    public async measureEnvironment(durationMs = 2000): Promise<EnvironmentResult> {
        if (!this.mediaStream) throw new Error('Microphone not initialized');

        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        const ctx = new AudioCtx();
        if (ctx.state === 'suspended') await ctx.resume();
        const source = ctx.createMediaStreamSource(this.mediaStream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        source.connect(analyser);

        const buf = new Float32Array(analyser.fftSize);
        let maxPeak = 0;
        let clipping = false;
        const measurements: number[] = [];
        const t0 = Date.now();

        await new Promise<void>((resolve) => {
            const tick = () => {
                if (Date.now() - t0 >= durationMs) { resolve(); return; }
                analyser.getFloatTimeDomainData(buf);
                const rms = calculateRMS(buf);
                let peak = 0;
                for (let i = 0; i < buf.length; i++) peak = Math.max(peak, Math.abs(buf[i]));
                measurements.push(rms);
                maxPeak = Math.max(maxPeak, peak);
                if (detectClipping(buf)) clipping = true;
                requestAnimationFrame(tick);
            };
            tick();
        });

        source.disconnect();
        ctx.close();

        const avg = measurements.length > 0 ? measurements.reduce((a, b) => a + b, 0) / measurements.length : 0;
        this.envNoiseFloor = avg;
        this.vadThreshold = Math.max(0.02, avg * 2.5);

        let quality: 'quiet' | 'moderate' | 'noisy';
        let score: number;
        if (avg < 0.01) { quality = 'quiet'; score = 95; }
        else if (avg < 0.05) { quality = 'moderate'; score = 70; }
        else { quality = 'noisy'; score = Math.max(20, 50 - avg * 500); }
        this.envScore = Math.round(score);

        return { rmsLevel: avg, peakLevel: maxPeak, hasClipping: clipping, quality, score: this.envScore };
    }

    /* ── VAD (음성 구간 감지) ── */

    private isVoiceActive(rms: number): boolean {
        this.recentRms.push(rms);
        if (this.recentRms.length > 10) this.recentRms.shift();
        return rms > this.vadThreshold;
    }

    /* ── 목표음 제어 ── */

    public setTarget(midi: number) {
        this.targetMidi = midi;
        this.holdAccumulated = 0;
        this.holdStartTime = 0;
        this.lastInTarget = false;
    }

    public clearTarget() {
        this.targetMidi = 0;
        this.holdAccumulated = 0;
        this.holdStartTime = 0;
        this.lastInTarget = false;
    }

    /* ── 실시간 감지 시작 ── */

    public async start() {
        if (!this.mediaStream) throw new Error('Microphone not initialized');
        if (this.running) return;

        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        this.audioContext = new AudioCtx({ sampleRate: this.config.sampleRate });

        // iOS Safari autoplay policy: resume suspended context
        if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
        }

        this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);

        this.scriptProcessor = this.audioContext.createScriptProcessor(this.config.bufferSize, 1, 1);
        this.sourceNode.connect(this.scriptProcessor);
        this.scriptProcessor.connect(this.audioContext.destination);

        this.running = true;
        this.totalFrames = 0;
        this.voicedFrames = 0;
        this.confidenceSum = 0;

        this.scriptProcessor.onaudioprocess = (ev) => {
            if (!this.running) return;
            const input = ev.inputBuffer.getChannelData(0);
            const rms = calculateRMS(input);
            const pitch = detectPitchYIN(input, this.audioContext!.sampleRate);

            this.totalFrames++;

            let hz = 0, midi = 0, noteName = '', confidence = 0, cents = 0, octave = 0, noteNumber = 0;
            const isVoice = this.isVoiceActive(rms);

            if (pitch && isVoice && pitch.confidence >= this.config.minConfidence) {
                hz = pitch.frequency;
                midi = pitch.midi;
                noteName = pitch.noteName;
                confidence = pitch.confidence;
                cents = pitch.cents;
                octave = pitch.octave;
                noteNumber = pitch.noteNumber;
                this.voicedFrames++;
                this.confidenceSum += confidence;
            }

            // 목표음 판정
            let isInTarget = false;
            let holdProgress = 0;

            if (this.targetMidi > 0 && hz > 0) {
                const exactMidi = hzToMidi(hz);
                const diff = Math.abs(exactMidi - this.targetMidi);
                isInTarget = diff <= this.config.targetTolerance;

                if (isInTarget) {
                    const now = performance.now();
                    if (!this.lastInTarget) {
                        this.holdStartTime = now;
                    }
                    this.holdAccumulated = now - this.holdStartTime;
                    holdProgress = Math.min(1, this.holdAccumulated / this.config.holdDuration);

                    if (this.holdAccumulated >= this.config.holdDuration) {
                        if (this.noteHeldCallback) this.noteHeldCallback(this.targetMidi);
                        this.holdAccumulated = 0;
                        this.holdStartTime = 0;
                    }
                } else {
                    this.holdAccumulated = 0;
                    this.holdStartTime = 0;
                }
            } else if (this.targetMidi > 0) {
                this.holdAccumulated = 0;
                this.holdStartTime = 0;
            }

            this.lastInTarget = isInTarget;

            // Zero output
            const out = ev.outputBuffer.getChannelData(0);
            for (let i = 0; i < out.length; i++) out[i] = 0;

            // 쓰로틀링: 최소 간격 이내면 콜백 스킵
            const now = performance.now();
            if (now - this.lastRenderTime < this.config.renderThrottle) return;
            this.lastRenderTime = now;

            if (this.pitchCallback) {
                this.pitchCallback({ hz, midi, noteName, rms, confidence, isInTarget, holdProgress, cents, isVoice, octave, noteNumber });
            }
        };
    }

    /* ── 감지 중지 ── */

    public stop() {
        this.running = false;
        if (this.scriptProcessor && this.sourceNode) {
            try { this.sourceNode.disconnect(this.scriptProcessor); this.scriptProcessor.disconnect(); } catch {}
        }
        this.scriptProcessor = null;
    }

    /* ── 마이크 및 컨텍스트 해제 ── */

    public destroy() {
        this.stop();
        if (this.mediaStream) { this.mediaStream.getTracks().forEach(t => t.stop()); this.mediaStream = null; }
        if (this.audioContext) { this.audioContext.close(); this.audioContext = null; }
        this.sourceNode = null;
    }

    /* ── 결과 산출 ── */

    public calculateResult(successNotes: number[], gender?: string): TestResult {
        const sorted = [...successNotes].sort((a, b) => a - b);
        const lowest = sorted.length > 0 ? sorted[0] : 0;
        const highest = sorted.length > 0 ? sorted[sorted.length - 1] : 0;

        let comfLow = lowest, comfHigh = highest;
        if (sorted.length >= 4) {
            comfLow = sorted[Math.floor(sorted.length * 0.2)];
            comfHigh = sorted[Math.ceil(sorted.length * 0.8) - 1];
        }

        let voiceType = '';
        if (gender === 'female') {
            if (highest >= 79) voiceType = 'soprano';
            else if (highest >= 74) voiceType = 'mezzo-soprano';
            else voiceType = 'alto';
        } else {
            if (highest >= 72) voiceType = 'tenor';
            else if (highest >= 64) voiceType = 'baritone';
            else voiceType = 'bass';
        }

        const voicedRatio = this.totalFrames > 0 ? this.voicedFrames / this.totalFrames : 0;
        const avgConf = this.voicedFrames > 0 ? this.confidenceSum / this.voicedFrames : 0;
        const qualityScore = Math.min(100, Math.round(avgConf * 50 + voicedRatio * 30 + 20));

        return {
            lowestNote: lowest,
            highestNote: highest,
            comfortableLow: comfLow,
            comfortableHigh: comfHigh,
            voiceType,
            qualityScore,
            environmentScore: this.envScore,
            allSuccessNotes: sorted,
        };
    }
}

export { PitchResult, midiToNoteName, midiToHz, hzToNote };
export default AudioEngine;
